import React, { createContext, useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { UnmountClosed as Collapse } from 'react-collapse';

import { AccordionContext } from '.';
import './_accordion.scss';
import Checkmark from '@/modules/checkmark';

const AccordionSectionContext = createContext<any>(null);
interface IAccordionSection {
  children: React.ReactChild;
  title: string;
  sectionIndex: number;
  onHeaderClick: (sectionIndex: any, event: any) => void;
  onCompletion: () => void;
  setSectionRef: (sectionRef: any) => void;
}

const AccordionSection = (props: IAccordionSection) => {
  // This component relies on cloning to provide these props, and for
  // this reason they cannot be required. Provide sensible defaults instead.
  const [isComplete, setIsComplete] = useState(false);
  const [summary, setSummary] = useState('');

  const sectionRef = useRef<HTMLDivElement>(null);
  const { title, children: sectionContent, sectionIndex } = props;

  const openThisSection = (event: any) => {
    event.preventDefault();
    props.onHeaderClick(sectionIndex, event);
  };

  const completeSection = (summary: any) => {
    props.onCompletion();
    setIsComplete(true);
    setSummary(summary);
  };

  useEffect(() => {
    props.setSectionRef(sectionRef);
  }, []);

  return (
    <AccordionContext.Consumer>
      {({ activeSectionIndex }) => {
        const isActive = sectionIndex === activeSectionIndex;
        const isFutureSection = sectionIndex > activeSectionIndex;
        const isGreyedOut = isFutureSection && !isComplete;
        const isCompletedAndClosed = isComplete && !isActive;

        const containerClasses = classNames(
          'component-accordion__section',
          isGreyedOut && 'component-accordion__section--is-greyed-out'
        );
        const indicatorClasses = classNames(
          'component-accordion__section__header__indicator',
          isComplete && 'component-accordion__section__header__indicator--is-complete',
          isGreyedOut && 'component-accordion__section__header__indicator--is-greyed-out'
        );
        const titleClasses = classNames(
          'component-accordion__section__header__title',
          isGreyedOut && 'component-accordion__section__header__title--is-greyed-out'
        );
        const headerClasses = classNames(
          'component-accordion__section__header',
          isCompletedAndClosed === false && 'component-accordion__section__header--noninteractive'
        );

        return (
          <div className={`ys-card ${containerClasses}`} ref={sectionRef}>
            <a
              onClick={(e) => (isCompletedAndClosed ? openThisSection(e) : null)}
              href="#"
              className={headerClasses}
            >
              <div className={indicatorClasses}>
                {isComplete ? <Checkmark /> : sectionIndex + 1}
              </div>
              <div className="component-accordion__section__header__content">
                <h1 className={titleClasses}>{title}</h1>
                {isCompletedAndClosed && summary && (
                  <p className="component-accordion__section__header__summary">{summary}</p>
                )}
              </div>
              {isCompletedAndClosed && (
                <span className="component-accordion__section__header__link">Rediger</span>
              )}
            </a>
            <Collapse isOpened={isActive}>
              <section className="component-accordion__section__content">
                <div className="component-accordion__section__content__inner">
                  <AccordionSectionContext.Provider
                    value={{
                      completeSection: completeSection
                    }}
                  >
                    <div>{sectionContent}</div>
                  </AccordionSectionContext.Provider>
                </div>
              </section>
            </Collapse>
          </div>
        );
      }}
    </AccordionContext.Consumer>
  );
};

export { AccordionSection, AccordionSectionContext };
